
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-flame text-baby-powder hover:bg-flame/90 dark:text-baby-powder",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 dark:text-destructive-foreground",
        outline:
          "border border-slate-gray-light/30 bg-transparent hover:bg-slate-gray-light/10 text-black dark:text-white dark:border-gray-600",
        secondary:
          "bg-slate-gray-light/10 text-slate-gray-dark hover:bg-slate-gray-light/20 dark:text-white",
        ghost: "hover:bg-slate-gray-light/10 hover:text-black dark:hover:text-white dark:text-gray-200",
        link: "text-flame underline-offset-4 hover:underline dark:text-flame",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 rounded-full px-3",
        lg: "h-11 rounded-full px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
